/* eslint-disable no-nested-ternary */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Grid, Snackbar, ThemeProvider, Typography, useTheme,
} from '@mui/material';
import { Box } from '@mui/system';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { capitalCase } from 'change-case';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ContentTheme } from './ContentTheme';
import { Content, SearchInteractionAction } from '../../../common/models/content/Content';
import {
  getHeaderImageUrl, getHeading, getHeroFormat, getImageUrl,
} from './ContentCommonFunctions';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import ContentCard from './ContentCard';
import ContentHorizontalCardSkeleton from './ContentHorizontalCardSkeleton';
import { useIsMobile } from '../../../common/hooks/useResponsive';
import { useDisplayContentView } from '../../../common/hooks/useDisplayContentView';
import { useTrackElementVisibilityInViewPort } from '../../../common/hooks/useTrackElementVisibilityInViewPort';
import SavedContentEmptyStateView from './SavedContentEmptyStateView';
import ContentViewMoreHeader from './ContentViewMoreHeader';
import OptimusButton from '../../../common/library/Button/Button';
import { RESOURCES_ROUTE } from '../../../routes/namedRoutes';
import { TestIds } from '../../../assets/testIds';

interface ContentViewMorePageProps {
  categoryId: string;
  storeContentList: Content[];
  showBackBtn?: boolean;
  showSearchBar?: boolean;
  showTradeName?: boolean;
  fullWidthContainer?: boolean;
  hasMoreContent: boolean;
  topTradeName?: string;
  onNextPage?: () => void;
  isSavedPage?: boolean;
}

export default function ContentViewMoreContainer({
  categoryId,
  fullWidthContainer = false,
  showSearchBar = true,
  showBackBtn = true,
  showTradeName = true,
  storeContentList,
  hasMoreContent,
  topTradeName,
  onNextPage,
  isSavedPage,
}: ContentViewMorePageProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const isMobile = useIsMobile();
  const displayContentView = useDisplayContentView();
  const trackElementVisibility = useTrackElementVisibilityInViewPort();
  const { trackSearchInteraction } = useCustomEventTracking();
  const endMessageRef = useRef(null);
  const [showSavedItemsEmptyState, setShowSavedItemsEmptyState] = useState(false);

  useEffect(() => {
    if (storeContentList.length === 0) {
      setShowSavedItemsEmptyState(true);
    } else {
      setShowSavedItemsEmptyState(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeContentList]);

  // Track when the user reaches the end of the feed
  useEffect(() => {
    const endMessageElement = endMessageRef.current as unknown as Element;

    const observers = trackElementVisibility([endMessageElement], () => {
      if (endMessageRef.current) {
        trackSearchInteraction(
          'View',
          'ContentEnd',
          {
            action: SearchInteractionAction.ReachEnd,
          },
        );
      }
    });
    // Clean up the observers when the component unmounts
    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setOpenSnackbar(false);
  };

  const ScrollEndMessage = (
    <>{!isSavedPage && <Box
      id="endMessage"
      ref={endMessageRef}
      sx={{
        textAlign: 'center',
        fontSize: theme.spacing(2.25),
        mt: theme.spacing(12.5),
        color: theme.palette.grey[500],
        [theme.breakpoints.down('md')]: {
          ml: theme.spacing(4),
        },
      }}
    >
      <b>
        <Trans
          i18nKey="content.feed.endMessage"
          components={{
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            topAnchorLink: <a href="#top-anchor"
              aria-label={t('content.feed.useSearch') || ''}/>,
          }}
        />
      </b>
    </Box>}
    </>
  );

  const ScrollSkeleton = (
    <ContentHorizontalCardSkeleton
      sx={fullWidthContainer ? { paddingLeft: 0 } : {}}
    />
  );

  const ContentViews = useMemo(() => (storeContentList || [])
    .map((contentItem: Content) => {
      const isPoll = contentItem.attributes?.type?.toUpperCase() === 'POLL';
      return (<Grid
        item
        lg={isPoll && 7}
        md={isPoll && 8}
        sm={isPoll && 8}
        xs={isPoll && 12}
        key={`content-list-${contentItem.id}`}
        id={`optimus-content-${contentItem.id}`}
        sx={{
          paddingTop: `${theme.spacing(4)} !important`,
          paddingLeft: `${theme.spacing(4)} !important`,
          ...(isPoll && {
            justifySelf: 'center',
            minWidth: {
              lg: theme.spacing(100),
              md: theme.spacing(80),
              sm: theme.spacing(80),
              xs: '100%',
            },
          }),
        }}
      >
        <ContentCard
          key={contentItem.id}
          contentId={contentItem.id}
          contentHeroListener={() => displayContentView(contentItem)}
          contentText={contentItem.attributes?.title}
          contentShortDescription={contentItem.attributes?.short_description}
          categoryText={contentItem.attributes?.type}
          banner={
            {
              url: getImageUrl(
                getHeroFormat(contentItem)?.url
                ,
              ),
              width: getHeroFormat(contentItem)?.width,
              height: getHeroFormat(contentItem)?.height,
            }}
          authorTitle={getHeading(contentItem)}
          publishedDate={contentItem.attributes?.source_published_date}
          logo={getHeaderImageUrl(contentItem)}
          videoUrl={contentItem.attributes?.video_url}
          contentLikeStatus={contentItem.likeStatus}
          formId={contentItem.attributes?.form_id}
          metadata={contentItem.attributes?.metadata}
          podcast={contentItem.attributes?.podcast}
          contentFeedSection={contentItem.contentFeedSection}
          contentFeedSectionPosition={contentItem.contentFeedSectionPosition}
          locked={contentItem.attributes?.locked}
          lowestMembershipRequired={contentItem.attributes?.lowestMembershipRequired}
          isHorizontalCard={!isMobile}
        />
      </Grid>);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [storeContentList, fullWidthContainer, theme]);

  return (
    <ThemeProvider theme={ContentTheme}>
      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={handleClose}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>
      <Box
        id='content-view-more-page'
        sx={{
          [theme.breakpoints.up('md')]: {
            padding: theme.spacing(4),
          },
        }}
      >
        {!isSavedPage &&
          <ContentViewMoreHeader showBackBtn={showBackBtn} showSearchBar={showSearchBar} />}
        <Box
          id='content-view-more-container'
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            alignSelf: 'stretch',
          }}
        >
          {isSavedPage &&
            <OptimusButton
              data-testid={TestIds.contentFeed.savedBackBtn}
              customVariant='secondaryGray'
              size='s'
              fullWidth={false}
              leadingIcon={ChevronLeftIcon}
              buttonText={`${t('button.backToResources')}`}
              onClick={() => navigate(RESOURCES_ROUTE)}
              sx={{ minWidth: 0, mb: 2.5 }}
            />
          }
          {showTradeName &&
            <Typography
              variant="h12"
              sx={{
                marginBottom: theme.spacing(4),
                [theme.breakpoints.down('md')]: {
                  marginBottom: theme.spacing(2),
                },
              }}

            >
              {isSavedPage ? 'Saved' : (topTradeName ?
                `Results for '${capitalCase(topTradeName)}'` : `Results for '${capitalCase(categoryId)}'`)}
            </Typography>
          }
          {isSavedPage && showSavedItemsEmptyState ? (
            <SavedContentEmptyStateView />
          ) : (
            <Grid container spacing={4}>
              <Box sx={{ width: '100%' }}>
                <InfiniteScroll
                  scrollThreshold={0.8}
                  dataLength={storeContentList.length}
                  // eslint-disable-next-line @typescript-eslint/no-empty-function
                  next={onNextPage || (() => { })}
                  hasMore={hasMoreContent}
                  style={{ overflow: 'hidden' }}
                  loader={ScrollSkeleton}
                  endMessage={ScrollEndMessage}
                >
                  {ContentViews}
                </InfiniteScroll>
              </Box>
            </Grid>
          )}

        </Box>
      </Box>
    </ThemeProvider >
  );
}
